import React from 'react';
import { motion } from 'framer-motion';
import { Quote } from 'lucide-react';
import SlideWrapper from '../SlideWrapper';
import { useLanguage } from '../../context/LanguageContext';

const thesisCopy = {
  en: {
    line1Prefix: 'For',
    line1Middle: 'of the last',
    line1Suffix: 'years,',
    line2Prefix: 'humans thrived',
    line2Accent: 'without markets',
    body: 'We shared. We gifted. We built networks of mutual obligation that stretched across continents. Then something cracked.',
    stat1: 'years of gift economies',
    stat2: 'years of transition',
    stat3: 'years of pure market dominance',
  },
  de: {
    line1Prefix: 'Fuer',
    line1Middle: 'der letzten',
    line1Suffix: 'Jahre,',
    line2Prefix: 'lebten Menschen erfolgreich',
    line2Accent: 'ohne Maerkte',
    body: 'Wir teilten. Wir schenkten. Wir bauten Netzwerke gegenseitiger Verpflichtung ueber Kontinente hinweg. Dann bekam das System einen Riss.',
    stat1: 'Jahre Geschenkoekonomien',
    stat2: 'Jahre Uebergang',
    stat3: 'Jahre reine Marktdominanz',
  },
  es: {
    line1Prefix: 'Durante',
    line1Middle: 'de los ultimos',
    line1Suffix: 'anos,',
    line2Prefix: 'la humanidad prospero',
    line2Accent: 'sin mercados',
    body: 'Compartimos. Regalamos. Construimos redes de obligacion mutua que cruzaban continentes. Luego algo se rompio.',
    stat1: 'anos de economias del don',
    stat2: 'anos de transicion',
    stat3: 'anos de dominio puro del mercado',
  },
};

export default function ThesisSlide({ refCallback }) {
  const { locale } = useLanguage();
  const copy = thesisCopy[locale] || thesisCopy.en;

  return (
    <SlideWrapper id="thesis" refCallback={refCallback} className="text-[#1a243f] dark:text-[#e6e8f4]">
      <div className="text-center max-w-4xl mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          transition={{ duration: 1 }}
          className="mb-12"
        >
          <Quote size={48} className="text-amber-300 mx-auto mb-6" />
          <h2 className="text-3xl md:text-5xl lg:text-6xl font-black leading-tight text-gray-900 dark:text-white mb-8">
            {copy.line1Prefix} <span className="text-emerald-600 dark:text-emerald-400">90,000</span> {copy.line1Middle}{' '}
            <span className="text-amber-600 dark:text-amber-400">100,000</span> {copy.line1Suffix}
            <br />
            {copy.line2Prefix} <span className="underline decoration-emerald-400 decoration-4 underline-offset-4">{copy.line2Accent}</span>.
          </h2>
          <p className="text-xl md:text-2xl text-gray-600 dark:text-gray-300 leading-relaxed max-w-3xl mx-auto">
            {copy.body}
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 40 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.5, duration: 0.8 }}
          className="grid grid-cols-1 md:grid-cols-3 gap-6"
        >
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur rounded-2xl p-6 shadow-lg border border-emerald-200 dark:border-emerald-800">
            <div className="text-4xl font-black text-emerald-600 dark:text-emerald-400 mb-2">90k</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">{copy.stat1}</div>
          </div>
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur rounded-2xl p-6 shadow-lg border border-amber-200 dark:border-amber-800">
            <div className="text-4xl font-black text-amber-600 dark:text-amber-400 mb-2">10k</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">{copy.stat2}</div>
          </div>
          <div className="bg-white/80 dark:bg-gray-800/80 backdrop-blur rounded-2xl p-6 shadow-lg border border-red-200 dark:border-red-800">
            <div className="text-4xl font-black text-red-600 dark:text-red-400 mb-2">~250</div>
            <div className="text-sm text-gray-600 dark:text-gray-400">{copy.stat3}</div>
          </div>
        </motion.div>
      </div>
    </SlideWrapper>
  );
}
